export function normalizeDigits(value: string): string {
  return value.replace(/\D/g, '');
}

// Portuguese NIF: 9 digits with Mod 11 checksum.
// Valid first digits: 1, 2, 3 (singular person), 5 (collective), 6 (public administration),
// 7 (heritage / undivided estate), 8 (sole trader), 9 (irregular collective).
// 4 is reserved/unused; 0 is invalid.
export function validateNIF(value: string): boolean {
  const digits = normalizeDigits(value);
  if (digits.length !== 9) return false;
  if (!/^[1-35-9]/.test(digits)) return false;

  let sum = 0;
  for (let i = 0; i < 8; i++) {
    sum += Number(digits[i]) * (9 - i);
  }
  const mod = sum % 11;
  const checkDigit = mod < 2 ? 0 : 11 - mod;
  return checkDigit === Number(digits[8]);
}

// Portuguese postal code: NNNN-NNN
export function validatePostalCode(value: string): boolean {
  return /^\d{4}-\d{3}$/.test(value.trim());
}

// Portuguese phone: 9 digits, optionally prefixed with +351 / 00351 / 351.
// Mobile starts with 9; landline starts with 2.
export function validatePhonePT(value: string): boolean {
  const digits = normalizeDigits(value)
    .replace(/^00351/, '')
    .replace(/^351/, '');
  return /^[29]\d{8}$/.test(digits);
}

// Portuguese NISS (social security number): 11 digits.
export function validateNISS(value: string): boolean {
  const digits = normalizeDigits(value);
  return digits.length === 11;
}

// Pragmatic email check: requires local-part, @, host with at least one dot, and a 2+ char TLD.
// Stricter than HTML5's built-in (which accepts "a@b") but still permissive enough for real-world addresses.
export function validateEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

export function todayISODate(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDate(value: string): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfToday(): Date {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}

// Birth date must be a real date, in the past, and within the last 120 years.
export function birthDateError(value: string): string | null {
  if (!value) return null;
  const d = parseDate(value);
  if (!d) return 'Data inválida.';
  const today = startOfToday();
  if (d > today) return 'A data de nascimento não pode estar no futuro.';
  const oldest = new Date(today);
  oldest.setFullYear(today.getFullYear() - 120);
  if (d < oldest) return 'Data de nascimento implausível.';
  return null;
}

// Event date must be today or later (for noise permits, venue bookings, etc.).
export function futureDateError(value: string): string | null {
  if (!value) return null;
  const d = parseDate(value);
  if (!d) return 'Data inválida.';
  if (d < startOfToday()) return 'A data não pode estar no passado.';
  return null;
}

// HH:MM string comparison works lexically.
export function timeRangeError(start: string, end: string): string | null {
  if (!start || !end) return null;
  if (end <= start) return 'A hora de fim deve ser posterior à hora de início.';
  return null;
}
